import type { DocumentDTO } from "@zplanem/responses"
import { useState } from "react"
import { DocumentAiReviewModal } from "../../components/documents/document-ai-review-modal"
import { DocumentEditModal } from "../../components/documents/document-edit-modal"
import { DocumentGrantsModal } from "../../components/documents/document-grants-modal"
import { DocumentList } from "../../components/documents/document-list"
import { DocumentUploadModal } from "../../components/documents/document-upload-modal"
import { useProjectDocuments } from "../../hooks/use-documents"
import { getTranslations } from "../../i18n"
import { useLocale } from "../../locale-context"
import { useProjectDetailContext } from "./project-detail-context"

function DocumentsIcon() {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.5"
			strokeLinecap="round"
			strokeLinejoin="round"
			className="w-10 h-10"
			aria-hidden="true"
		>
			<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
			<polyline points="14 2 14 8 20 8" />
			<line x1="16" y1="13" x2="8" y2="13" />
			<line x1="16" y1="17" x2="8" y2="17" />
			<polyline points="10 9 9 9 8 9" />
		</svg>
	)
}

function PlusIcon() {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.5"
			strokeLinecap="round"
			className="w-5 h-5"
			aria-hidden="true"
		>
			<path d="M12 5v14M5 12h14" />
		</svg>
	)
}

export function ProjectDocumentsTab() {
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const td = t.projects.documents
	const { propertyId, project } = useProjectDetailContext()

	const scope = { type: "project" as const, propertyId, projectId: project.id }
	const { data, isLoading, isError, error } = useProjectDocuments(
		propertyId,
		project.id,
	)

	const [uploadOpen, setUploadOpen] = useState(false)
	const [grantsDoc, setGrantsDoc] = useState<DocumentDTO | null>(null)
	const [editingDoc, setEditingDoc] = useState<DocumentDTO | null>(null)
	const [reviewingDoc, setReviewingDoc] = useState<DocumentDTO | null>(null)

	if (isLoading) {
		return (
			<div className="py-12 text-center text-sm text-muted-foreground">
				{t.common.loading}
			</div>
		)
	}

	const items = data ?? []

	return (
		<div className="space-y-4">
			{items.length === 0 && !isError ? (
				<div className="rounded-xl border border-dashed border-border bg-muted/30 px-6 py-16 flex flex-col items-center text-center">
					<div className="text-muted-foreground/70 mb-3">
						<DocumentsIcon />
					</div>
					<h2 className="text-base font-bold text-foreground">
						{td.emptyTitle}
					</h2>
					<p className="mt-1.5 text-sm text-muted-foreground/80 max-w-sm">
						{td.emptyBody}
					</p>
					<button
						type="button"
						onClick={() => setUploadOpen(true)}
						className="mt-6 inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-foreground/80 hover:text-foreground transition-colors"
					>
						<PlusIcon />
						{td.addCta}
					</button>
				</div>
			) : (
				<>
					<div className="flex items-center justify-end">
						<button
							type="button"
							onClick={() => setUploadOpen(true)}
							className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground/80 hover:text-foreground transition-colors"
						>
							<PlusIcon />
							{td.addCta}
						</button>
					</div>
					<DocumentList
						documents={items}
						scope={scope}
						onOpenGrants={setGrantsDoc}
						onEdit={setEditingDoc}
						onReviewAiClick={setReviewingDoc}
						error={isError ? error : undefined}
					/>
				</>
			)}

			{uploadOpen ? (
				<DocumentUploadModal
					propertyId={propertyId}
					fixedScope={scope}
					onClose={() => setUploadOpen(false)}
				/>
			) : null}
			{grantsDoc ? (
				<DocumentGrantsModal
					propertyId={propertyId}
					document={grantsDoc}
					onClose={() => setGrantsDoc(null)}
				/>
			) : null}
			{editingDoc ? (
				<DocumentEditModal
					propertyId={propertyId}
					scope={scope}
					document={editingDoc}
					onClose={() => setEditingDoc(null)}
				/>
			) : null}
			{reviewingDoc ? (
				<DocumentAiReviewModal
					propertyId={propertyId}
					document={reviewingDoc}
					onClose={() => setReviewingDoc(null)}
				/>
			) : null}
		</div>
	)
}
